import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import { fetchDemMasterVersions } from '../../lib/demCustomers';
import {
  syncDemCustomersFromPowerBi,
  syncDemItemsFromPowerBi,
  syncDemPricesFromPowerBi,
} from '../../lib/demStandardSalesSync';
import type { DemMasterVersion, DemMasterVersionKey } from '../../types/dem';
import {
  formatMasterDataVersion,
  formatMasterDataVersionRelative,
  parseMasterDataVersionUnix,
} from '../../utils/dateTime';

type PowerBiSyncKind = 'prices' | 'items' | 'customers';

export function DemMasterDataPage({ embedded = false }: { embedded?: boolean }) {
  const [versions, setVersions] = useState<DemMasterVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<PowerBiSyncKind | null>(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDemMasterVersions();
      setVersions(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read cloud versions.');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  useDemMasterDataRealtime({
    include: ['system_config', 'customers', 'items', 'prices', 'standard_sales_lines'],
    onChange: () => void loadVersions(),
  });

  const versionFor = (key: DemMasterVersionKey) =>
    versions.find((v) => v.key === key)?.version ?? null;

  const latestSync = useMemo(() => {
    let latest: { version: string; date: Date } | null = null;
    for (const entry of versions) {
      const d = parseMasterDataVersionUnix(entry.version);
      if (!d || !entry.version) continue;
      if (!latest || d > latest.date) {
        latest = { version: entry.version, date: d };
      }
    }
    return latest;
  }, [versions]);

  const latestSyncLabel = latestSync ? formatMasterDataVersion(latestSync.version) : null;
  const latestSyncRelative = latestSync ? formatMasterDataVersionRelative(latestSync.version) : null;

  const runPowerBiSync = async (kind: PowerBiSyncKind) => {
    setBusyKind(kind);
    setNotice(null);
    setError(null);
    try {
      const result =
        kind === 'prices'
          ? await syncDemPricesFromPowerBi()
          : kind === 'items'
            ? await syncDemItemsFromPowerBi()
            : await syncDemCustomersFromPowerBi();
      const label =
        kind === 'prices' ? 'Prices' : kind === 'items' ? 'Items' : 'Customers';
      const secs =
        typeof result.duration_ms === 'number'
          ? ` in ${(result.duration_ms / 1000).toFixed(1)}s`
          : '';
      let extra = '';
      if (kind === 'prices' && typeof result.fetched === 'number') {
        extra = ` (read ${result.fetched.toLocaleString()}${
          result.source_total != null ? ` / ${Number(result.source_total).toLocaleString()} source` : ''
        }, skipped expired ${Number(result.skipped_expired ?? 0).toLocaleString()})`;
      } else if (kind === 'items' && typeof result.fetched === 'number') {
        extra = ` (read ${result.fetched.toLocaleString()}, skipped IPG ${Number(
          result.skipped_ipg ?? 0,
        ).toLocaleString()}, deactivated ${Number(result.deactivated ?? 0).toLocaleString()})`;
      } else if (kind === 'customers' && typeof result.fetched === 'number') {
        extra = ` (read ${result.fetched.toLocaleString()}, skipped dim2≠10 ${Number(
          result.skipped_dim2 ?? 0,
        ).toLocaleString()}, preserved manual fields on ${Number(
          result.preserved ?? 0,
        ).toLocaleString()} rows, deactivated ${Number(result.deactivated ?? 0).toLocaleString()})`;
        if (result.soft_delete_skipped) {
          extra += ` — soft-delete skipped: ${result.soft_delete_skip_reason || 'import too small'}`;
        }
      }
      setNotice(`Synced ${result.rows?.toLocaleString() ?? '?'} ${label} from PowerBI${secs}${extra}.`);
      await loadVersions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PowerBI sync failed.');
    } finally {
      setBusyKind(null);
    }
  };

  const renderSslPortalCard = () => {
    const version = versionFor('standard_sales_lines_version');
    const syncedAt = version ? formatMasterDataVersion(version) : null;
    const relative = version ? formatMasterDataVersionRelative(version) : null;

    return (
      <div className="card dem-master-file-card dem-master-file-card--ssl">
        <div className="dem-master-file-card__icon" aria-hidden>
          ✎
        </div>
        <div className="dem-master-file-card__body">
          <div className="dem-master-file-card__title">Standard Sales Lines</div>
          {!embedded ? (
            <>
              <div className="dem-master-file-card__detail">
                Source: <strong>DEM portal</strong> (Std Lines)
              </div>
              <div className="dem-master-file-card__sub">
                Portal-managed — add and edit under DEM → Std Lines. PowerBI sync disabled.
              </div>
            </>
          ) : (
            <div className="dem-master-file-card__sub">
              Portal-managed (DEM → Std Lines). PowerBI sync off.
            </div>
          )}
        </div>
        <div className="dem-master-file-card__status dem-master-file-card__status--actions">
          {syncedAt ? (
            <>
              <span className="dem-master-file-card__status-date">{syncedAt}</span>
              {relative && <span className="dem-master-file-card__status-badge">{relative}</span>}
            </>
          ) : (
            <span className="dem-master-file-card__status-muted">No version stamp</span>
          )}
        </div>
      </div>
    );
  };

  const renderPowerBiCard = (opts: {
    kind: PowerBiSyncKind;
    title: string;
    source: string;
    mapping: string;
    versionKey: DemMasterVersionKey;
    scheduleNote: string;
  }) => {
    const version = versionFor(opts.versionKey);
    const syncedAt = version ? formatMasterDataVersion(version) : null;
    const relative = version ? formatMasterDataVersionRelative(version) : null;
    const busy = busyKind === opts.kind;

    return (
      <div className="card dem-master-file-card dem-master-file-card--ssl">
        <div className="dem-master-file-card__icon" aria-hidden>
          ⇄
        </div>
        <div className="dem-master-file-card__body">
          <div className="dem-master-file-card__title">{opts.title}</div>
          {!embedded ? (
            <>
              <div className="dem-master-file-card__detail">
                Source: PowerBI <code>{opts.source}</code>
              </div>
              <div className="dem-master-file-card__sub">{opts.mapping}</div>
              <div className="dem-master-file-card__sub">{opts.scheduleNote}</div>
            </>
          ) : null}
        </div>
        <div className="dem-master-file-card__status dem-master-file-card__status--actions">
          {syncedAt ? (
            <>
              <span className="dem-master-file-card__status-date">{syncedAt}</span>
              {relative && <span className="dem-master-file-card__status-badge">{relative}</span>}
            </>
          ) : (
            <span className="dem-master-file-card__status-muted">No sync record</span>
          )}
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busyKind != null || loading}
            onClick={() => void runPowerBiSync(opts.kind)}
          >
            {busy ? 'Syncing…' : 'Sync from PowerBI'}
          </button>
        </div>
      </div>
    );
  };

  const refreshBtn = (
    <button
      type="button"
      className="btn-secondary btn-sm"
      onClick={() => void loadVersions()}
      disabled={loading || busyKind != null}
    >
      Refresh versions
    </button>
  );

  const syncCards = (
    <div className={`dem-master-file-list${embedded ? ' dem-master-file-list--compact' : ''}`}>
      {renderPowerBiCard({
        kind: 'customers',
        title: 'Customer Master',
        source: 'sales.customer_list',
        mapping:
          'Maps to DEM customers (code←no, name, address←address+address_2+city+post_code, price_group←customer_price_group, is_blocked←blocked, salesperson_code, delivery_instruction, delivery_schedule, bill_to_customer_no). Only global_dimension_2_code=10. DEM status=Active while present in PowerBI; Inactive when code disappears, or salesperson_code is INACTIVE / BAD DEBT (portal_token cleared). Never uses PowerBI status field. Never overwrites email / min_order / portal_token on Active rows. After each sync, allowed_days is paired from delivery_schedule (NAV text) when parseable — DAILY / EVERY DAY = Mon–Fri (not Sat/Sun); common typos like THRUS / N.A are normalized.',
        versionKey: 'customer_version',
        scheduleNote: 'Auto-syncs daily at 06:45 SGT.',
      })}
      {renderPowerBiCard({
        kind: 'items',
        title: 'Item Master',
        source: 'purchasing.item_list',
        mapping:
          'Maps to DEM items (code←no, uom←sales_unit_of_measure, packing←std_ctn_packing, inventory, lid_code←no_3). Skips FG-AP/APT/DEL/GUL/PR/P/MO/QC. Preserves image_url.',
        versionKey: 'item_version',
        scheduleNote: 'Auto-syncs daily at 07:15 SGT.',
      })}
      {renderSslPortalCard()}
      {renderPowerBiCard({
        kind: 'prices',
        title: 'Sales Prices',
        source: 'sales.sales_prices',
        mapping:
          'Maps to DEM prices (item_code / price_type / price_code / dates / uom / min_qty / price). Skips expired ending_date rows.',
        versionKey: 'price_version',
        scheduleNote: 'Auto-syncs daily at 07:30 SGT.',
      })}
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className="synchronize-admin__master-toolbar">{refreshBtn}</div>
      ) : (
        <PageHeader
          title="Master Data"
          description="Sync status for master data already stored in Supabase."
          actions={refreshBtn}
        />
      )}

      {error && <p className="alert alert-error">{error}</p>}
      {notice && <p className="alert alert-success">{notice}</p>}

      {!embedded && latestSyncLabel ? (
        <div className="card dem-master-latest-sync" role="status">
          <span className="dem-master-latest-sync__label">Latest master data update</span>
          <strong className="dem-master-latest-sync__value">{latestSyncLabel}</strong>
          {latestSyncRelative && (
            <span className="dem-master-latest-sync__badge">{latestSyncRelative}</span>
          )}
        </div>
      ) : null}

      {embedded ? (
        syncCards
      ) : (
        <section className="dem-master-section">
          <h2 className="dem-master-section__title">PowerBI sync</h2>
          <p className="page-lead">
            Customers / Items: upsert + soft delete (manual customer fields preserved). Prices: full
            replace. Standard Sales Lines are edited in the portal (PowerBI sync off). Auto daily job
            + manual Sync when needed for the remaining tables.
          </p>
          {syncCards}
        </section>
      )}
    </>
  );
}
